import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { apiPost } from './api';
import { AuthContext, type AuthValue } from './auth';

/**
 * Development sign-in.
 *
 * core-api mints the token (ADR-0032); the browser never holds the signing
 * secret. The token does not expire within a session, so `getToken` simply
 * returns what is stored — the async signature exists for Clerk's sake, and
 * satisfying it costs nothing here.
 */
interface Stored {
  subject: string;
  token: string;
  membershipId: string | null;
}

const STORAGE_KEY = 'dc.session';

/**
 * Read once during the first render rather than in an effect.
 *
 * An effect would render signed-out, then immediately signed-in — a visible
 * flash of the sign-in screen on every reload. Storage can throw outright in a
 * private window, so every access is guarded and the app works without it.
 */
function readStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function write(value: Stored | null): void {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal: sign-in still works, it just will not survive a reload */
  }
}

export function LocalAuthProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<Stored | null>(readStored);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signInAs = useCallback(async (subject: string) => {
    setPending(true);
    setError(null);
    try {
      const { token } = await apiPost<{ token: string }>('/v1/dev/session', { subject });
      const next = { subject, token, membershipId: null };
      setStored(next);
      write(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setPending(false);
    }
  }, []);

  const value = useMemo<AuthValue & { signInAs: typeof signInAs }>(
    () => ({
      isSignedIn: stored !== null,
      label: stored?.subject ?? null,
      getToken: () => Promise.resolve(stored?.token ?? null),
      signOut: () => {
        setStored(null);
        write(null);
        setError(null);
      },
      membershipId: stored?.membershipId ?? null,
      setMembership: (membershipId) =>
        setStored((s) => {
          if (!s) return s;
          const next = { ...s, membershipId };
          write(next);
          return next;
        }),
      pending,
      error,
      signInAs,
    }),
    [stored, pending, error, signInAs],
  );

  return (
    <AuthContext.Provider value={value}>
      <LocalSignInContext.Provider value={value}>{children}</LocalSignInContext.Provider>
    </AuthContext.Provider>
  );
}

/**
 * `signInAs` is local-only, so it is carried on a separate context rather than
 * widened into AuthValue. Putting it on the shared interface would mean every
 * consumer can see a method that does nothing under Clerk — an affordance that
 * exists in one configuration and silently does not in another.
 */
const LocalSignInContext = createContext<(AuthValue & { signInAs: (s: string) => Promise<void> }) | null>(null);

/**
 * The seeded accounts, listed with what each one PROVES.
 *
 * Signing in as a school admin exists to watch the record plane refuse them.
 */
const SEEDED = [
  { subject: 'local_counselor', name: 'Nia Okafor', role: 'counselor · Riverbend High',
    proves: 'Caseload workspace. Devon returns 404, not 403.' },
  { subject: 'local_student', name: 'Maya Chen', role: 'student · Riverbend High',
    proves: 'Her own record, and no peer’s.' },
  { subject: 'local_school_admin', name: 'Ruth Delgado', role: 'school admin · Riverbend High',
    proves: 'Caseload assignment. The record plane refuses her.' },
  { subject: 'local_district_admin', name: 'Sam Whitfield', role: 'district admin · Riverbend USD',
    proves: 'The whole subtree, and no peer district.' },
  { subject: 'local_parent', name: 'Wei Chen', role: 'parent · Riverbend High',
    proves: 'No administrative plane at all.' },
  { subject: 'local_teacher', name: 'Lena Marsh', role: 'teacher · Riverbend High',
    proves: 'Her letter requests, and no student record.' },
  { subject: 'local_curator', name: 'Ines Bakhtiari', role: 'corpus curator · DreamCollege',
    proves: 'The corpus, and not one student. Approves; the API never publishes.' },
  { subject: 'local_operator', name: 'Rae Lindqvist', role: 'platform operator · DreamCollege',
    proves: 'Every tenant, and not one student record.' },
  { subject: 'local_platform_admin', name: 'Oz Farrell', role: 'platform admin · DreamCollege',
    proves: 'Break-glass: the role alone opens nothing.' },
  { subject: 'nobody_at_all', name: 'Unprovisioned', role: 'valid token, no account',
    proves: 'A signed token is not access: 401.' },
];

export function LocalSignIn() {
  const ctx = useContext(LocalSignInContext);
  if (!ctx) throw new Error('LocalSignIn requires LocalAuthProvider');

  return (
    <section className="signin">
      <header className="signin__head">
        <h2>Sign in</h2>
        <p className="muted">
          Development sign-in. core-api mints the token — the browser never holds the signing
          secret.
        </p>
      </header>

      {ctx.error && <p className="signin__error" role="alert">{ctx.error}</p>}

      <ul className="signin__list">
        {SEEDED.map((u) => (
          <li key={u.subject}>
            <button
              type="button"
              className="account"
              onClick={() => void ctx.signInAs(u.subject)}
              disabled={ctx.pending}
            >
              <span className="account__name">{u.name}</span>
              <span className="account__role">{u.role}</span>
              <span className="account__proves">{u.proves}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
